import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useInstructionsQuery } from "../../hooks/useLabelingQueries";
import { FaTimes } from "../../utils/icons";
import { logger } from "../../utils/logger";

interface InstructionsDialogProps {
  /**
   * Labeling project whose authenticated backend endpoint serves the
   * worklist-pinned S3 instructions. Omit only for non-project static surfaces
   * such as the reader-time study instructions.
   */
  projectId?: string | null;
  /**
   * The worklist's `instructions_key` (e.g. "us3d-renal-stone-v1.md"). The
   * backend resolves this key from the worklist when `projectId` is provided.
   * Static non-project surfaces use it as a bundled lookup key.
   *
   * Null/undefined means the worklist doesn't reference instructions; the
   * "Instructions" button shouldn't render in that case, but we still
   * handle it here defensively to keep the prop optional.
   */
  instructionsKey: string | null | undefined;
  /** Inline instructions already authenticated and bound to this task revision. */
  instructionsMarkdown?: string;
  open: boolean;
  onClose: () => void;
  /** Dialog heading. Defaults to "Annotation Instructions" (labeler); the
   *  reader study passes "Study instructions". */
  title?: string;
}

/**
 * Static instructions used outside a labeling project remain bundled. Project
 * instructions are fetched from the authenticated backend/S3 path and do not
 * need a frontend copy.
 */
const INSTRUCTIONS_BY_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>("../../constants/instructions/*.md", {
      query: "?raw",
      import: "default",
      eager: true,
    }),
  ).map(([path, md]) => [path.split("/").pop() ?? "", md]),
);

export function getInstructionsByKey(instructionsKey: string | null | undefined): string | null {
  if (!instructionsKey) return null;
  const md = INSTRUCTIONS_BY_KEY[instructionsKey];
  if (md) {
    logger.info("instructions_lookup key=%s matched=ok", instructionsKey);
    return md;
  }
  logger.info("instructions_lookup key=%s matched=none", instructionsKey);
  return null;
}

/** Visible for tests — every filename `import.meta.glob` actually loaded. */
export function listBundledInstructionsKeys(): string[] {
  return Object.keys(INSTRUCTIONS_BY_KEY).sort();
}

type InstructionsQueryResult = ReturnType<typeof useInstructionsQuery>;

interface InstructionsDialogContentProps extends InstructionsDialogProps {
  remoteInstructions?: InstructionsQueryResult;
}

const RemoteInstructionsDialog: React.FC<InstructionsDialogProps> = (props) => {
  const remoteInstructions = useInstructionsQuery(
    props.projectId ?? "",
    props.open && !!props.instructionsKey,
  );
  return <InstructionsDialogContent {...props} remoteInstructions={remoteInstructions} />;
};

const InstructionsDialogContent: React.FC<InstructionsDialogContentProps> = ({
  projectId = null,
  instructionsKey,
  instructionsMarkdown,
  open,
  onClose,
  title = "Annotation Instructions",
  remoteInstructions,
}) => {
  const usesRemoteInstructions = remoteInstructions !== undefined;
  const markdown =
    instructionsMarkdown ??
    (usesRemoteInstructions
      ? (remoteInstructions?.data ?? null)
      : getInstructionsByKey(instructionsKey));
  const dialogRef = React.useRef<HTMLDivElement>(null);

  const isMissingStaticBundle = !usesRemoteInstructions && !markdown && !!instructionsKey;
  const isRemoteError = usesRemoteInstructions && remoteInstructions?.isError === true;

  React.useEffect(() => {
    if (open && isMissingStaticBundle) {
      logger.warn(
        "instructions_dialog_missing_static_bundle key=%s — non-project surface has no matching bundled markdown.",
        instructionsKey,
      );
    }
    if (open && isRemoteError) {
      logger.warn(
        "instructions_dialog_remote_error project=%s key=%s — authenticated S3-backed instruction fetch failed.",
        projectId,
        instructionsKey,
      );
    }
  }, [open, isMissingStaticBundle, isRemoteError, projectId, instructionsKey]);

  // Escape-to-close + Tab focus trap + focus restore — matches the other
  // reader-study modals so the dialog behaves like a native modal for
  // keyboard and screen-reader users.
  React.useEffect(() => {
    if (!open) return;
    const container = dialogRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusable = (): HTMLElement[] =>
      container
        ? Array.from(
            container.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])',
            ),
          )
        : [];

    (focusable()[0] ?? container)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-xs flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="bg-bg-2 border border-border rounded shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="text-sm font-semibold text-white tracking-wide">{title}</h2>
          <button
            onClick={onClose}
            className="text-fg-muted hover:text-white transition-colors rounded-md p-1 hover:bg-white/5"
            aria-label="Close instructions"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {usesRemoteInstructions && remoteInstructions?.isLoading ? (
            <div
              className="flex items-center justify-center py-16"
              aria-label="Loading instructions"
            >
              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : markdown ? (
            <article
              className={
                instructionsMarkdown === undefined
                  ? "prose-instructions"
                  : "prose-instructions prose-instructions-task"
              }
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
            </article>
          ) : isRemoteError ? (
            <div className="text-sm text-fg bg-surface-2 border border-border-strong rounded-md px-4 py-3">
              <div className="font-semibold mb-1">Instructions unavailable</div>
              <div className="text-fg-muted">
                The project instructions could not be loaded. Your labeling work has not been
                changed. Close this dialog and try again; contact the a2z team if the problem
                continues.
              </div>
            </div>
          ) : isMissingStaticBundle ? (
            <div className="text-sm text-fg bg-surface-2 border border-border-strong rounded-md px-4 py-3">
              <div className="font-semibold mb-1">Instructions not bundled</div>
              <div className="text-fg-muted">
                This non-project surface references{" "}
                <code className="text-fg">{instructionsKey}</code>
                but no static instructions match. Contact the a2z team.
              </div>
            </div>
          ) : (
            <div className="text-sm text-fg-muted text-center py-8">
              No instructions available for this project.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const InstructionsDialog: React.FC<InstructionsDialogProps> = (props) =>
  props.projectId && props.instructionsMarkdown === undefined ? (
    <RemoteInstructionsDialog {...props} />
  ) : (
    <InstructionsDialogContent {...props} />
  );
